import React, { useState } from 'react';
import { ShareIcon, UsersIcon, HeartIcon, XIcon, CheckIcon, SearchIcon, PlusIcon } from 'lucide-react';
import { CreateGroup } from './CreateGroup';
type ShareStoryProps = {
  storyTitle: string;
  onClose: () => void;
  onShare: (recipients: string[]) => void;
};
type FriendSuggestion = {
  id: string;
  name: string;
  avatar: string;
  type: 'friend' | 'parent';
};
type Group = {
  id: string;
  name: string;
  avatar: string;
  members: string[];
};
export const ShareStory = ({
  storyTitle,
  onClose,
  onShare
}: ShareStoryProps) => {
  const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [activeTab, setActiveTab] = useState<'friends' | 'groups'>('friends');
  // Mock data - In real app, this would come from a database
  const [groups, setGroups] = useState<Group[]>([{
    id: 'g1',
    name: 'Story Club',
    avatar: '📚',
    members: ['2', '3', '5']
  }, {
    id: 'g2',
    name: 'Adventure Team',
    avatar: '🌟',
    members: ['1', '2', '4']
  }]);
  const suggestions: FriendSuggestion[] = [{
    id: '1',
    name: "Sarah's Parent",
    avatar: '👩',
    type: 'parent'
  }, {
    id: '2',
    name: 'Tommy',
    avatar: '🦁',
    type: 'friend'
  }, {
    id: '3',
    name: 'Lucy',
    avatar: '🦄',
    type: 'friend'
  }, {
    id: '4',
    name: "Alex's Parent",
    avatar: '👨',
    type: 'parent'
  }, {
    id: '5',
    name: 'Maya',
    avatar: '🐼',
    type: 'friend'
  }];
  const filteredSuggestions = suggestions.filter(friend => friend.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleShare = () => {
    onShare(selectedFriends);
    setIsShared(true);
    setTimeout(() => {
      onClose();
    }, 2000);
  };
  const toggleFriend = (friendId: string) => {
    setSelectedFriends(prev => prev.includes(friendId) ? prev.filter(id => id !== friendId) : [...prev, friendId]);
  };
  const handleSaveGroup = (group: Group) => {
    setGroups(prev => [...prev, group]);
    setShowCreateGroup(false);
  };
  const selectGroup = (group: Group) => {
    setSelectedFriends(group.members);
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-871">
      <div className="bg-white rounded-2xl w-full max-w-md p-6" data-id="element-872">
        <div className="flex items-center justify-between mb-6" data-id="element-873">
          <h2 className="text-xl font-bold text-purple-700" data-id="element-874">
            Share Your Story!
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-purple-100 rounded-full transition-colors" data-id="element-875">
            <XIcon size={20} className="text-purple-700" data-id="element-876" />
          </button>
        </div>
        {showCreateGroup && <CreateGroup onClose={() => setShowCreateGroup(false)} onSave={handleSaveGroup} data-id="element-877" />}
        {isShared ? <div className="text-center py-8" data-id="element-878">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-879">
              <CheckIcon size={32} className="text-green-500" data-id="element-880" />
            </div>
            <p className="text-lg font-medium text-purple-700" data-id="element-881">Story Shared!</p>
            <p className="text-purple-500 mt-2" data-id="element-882">
              Your friends will love reading your story!
            </p>
          </div> : <>
            <div className="mb-6" data-id="element-883">
              <p className="text-purple-600 mb-4" data-id="element-884">
                Share "{storyTitle}" with your friends and their parents!
              </p>
              {/* Tabs */}
              <div className="flex space-x-2 mb-4" data-id="element-885">
                <button onClick={() => setActiveTab('friends')} className={`flex-1 py-2 px-4 rounded-full transition-colors ${activeTab === 'friends' ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-600 hover:bg-purple-200'}`} data-id="element-886">
                  <UsersIcon size={18} className="inline-block mr-2" data-id="element-887" />
                  Friends
                </button>
                <button onClick={() => setActiveTab('groups')} className={`flex-1 py-2 px-4 rounded-full transition-colors ${activeTab === 'groups' ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-600 hover:bg-purple-200'}`} data-id="element-888">
                  <div size={18} className="inline-block mr-2" data-id="element-889" />
                  Groups
                </button>
              </div>
              {activeTab === 'friends' && <div className="relative" data-id="element-890">
                  <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search friends..." className="w-full pl-10 pr-4 py-2 border border-purple-200 rounded-full focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100" data-id="element-891" />
                  <SearchIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-400" data-id="element-892" />
                </div>}
            </div>
            <div className="max-h-64 overflow-y-auto mb-6" data-id="element-893">
              {activeTab === 'friends' ?
          // Friends List
          filteredSuggestions.map(friend => <button key={friend.id} onClick={() => toggleFriend(friend.id)} className={`w-full flex items-center p-3 rounded-xl mb-2 transition-colors ${selectedFriends.includes(friend.id) ? 'bg-purple-100 border-2 border-purple-300' : 'bg-purple-50 hover:bg-purple-100'}`} data-id="element-894">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-2xl" data-id="element-895">
                      {friend.avatar}
                    </div>
                    <div className="ml-3 flex-grow text-left" data-id="element-896">
                      <div className="font-medium text-purple-700" data-id="element-897">
                        {friend.name}
                      </div>
                      <div className="text-sm text-purple-500" data-id="element-898">
                        {friend.type === 'parent' ? 'Parent' : 'Friend'}
                      </div>
                    </div>
                    {selectedFriends.includes(friend.id) && <CheckIcon size={20} className="text-purple-600" data-id="element-899" />}
                  </button>) :
          // Groups List
          <>
                  <button onClick={() => setShowCreateGroup(true)} className="w-full flex items-center p-3 rounded-xl mb-4 bg-purple-50 hover:bg-purple-100 transition-colors" data-id="element-900">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center" data-id="element-901">
                      <PlusIcon size={24} className="text-purple-600" data-id="element-902" />
                    </div>
                    <div className="ml-3 font-medium text-purple-700" data-id="element-903">
                      Create New Group
                    </div>
                  </button>
                  {groups.map(group => <button key={group.id} onClick={() => selectGroup(group)} className={`w-full flex items-center p-3 rounded-xl mb-2 transition-colors ${group.members.every(m => selectedFriends.includes(m)) ? 'bg-purple-100 border-2 border-purple-300' : 'bg-purple-50 hover:bg-purple-100'}`} data-id="element-904">
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-2xl" data-id="element-905">
                        {group.avatar}
                      </div>
                      <div className="ml-3 flex-grow text-left" data-id="element-906">
                        <div className="font-medium text-purple-700" data-id="element-907">
                          {group.name}
                        </div>
                        <div className="text-sm text-purple-500" data-id="element-908">
                          {group.members.length} members
                        </div>
                      </div>
                      {group.members.every(m => selectedFriends.includes(m)) && <CheckIcon size={20} className="text-purple-600" data-id="element-909" />}
                    </button>)}
                </>}
            </div>
            <div className="flex justify-end space-x-3" data-id="element-910">
              <button onClick={onClose} className="px-4 py-2 rounded-full border-2 border-purple-200 text-purple-600 hover:bg-purple-50 transition-colors" data-id="element-911">
                Cancel
              </button>
              <button onClick={handleShare} disabled={selectedFriends.length === 0} className="px-6 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center" data-id="element-912">
                <ShareIcon size={20} className="mr-2" data-id="element-913" />
                Share Story
              </button>
            </div>
          </>}
      </div>
    </div>;
};